// ============================================================================
// NPSDPersonConnections.tsx - Connections management component for Person
// Converted from Vue 2 Vue-A2002-NPSDPersonConnections.ts
//
// Handles Person-to-Person (with relationship), Person-to-Vehicle,
// Person-to-Business, and Person-to-Client connections.
// Uses shared NPSDConnectionPanel component for each panel.
// ============================================================================
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { NPSDConnectionPanel } from '@/components/npsd/NPSDConnectionPanel';
import { removeFromArrayById } from '@/utils/arrays';

import { App } from '@/services/app-compat';
import { Codes } from '@/constants/codes';

interface DeleteConfirmState {
  open: boolean;
  type: string;
  item: any;
  name: string;
}

interface NPSDPersonConnectionsProps {
  person: any;
  isEditMode?: boolean;
  onAddConnection?: (type: string) => void;
  onViewConnection?: (type: string, item: any) => void;
  onConnectionRemoved?: (data: { type: string; id: any }) => void;
}

export function NPSDPersonConnections({
  person,
  isEditMode = false,
  onAddConnection,
  onViewConnection,
  onConnectionRemoved,
}: NPSDPersonConnectionsProps) {
  const { theme } = useTheme();

  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
    open: false,
    type: '',
    item: null,
    name: '',
  });

  const [relationshipTypes, setRelationshipTypes] = useState<any[]>([]);

  // Load relationship types on mount
  useEffect(() => {
    if (typeof Codes !== 'undefined' && typeof Codes.getRelationshipTypes === 'function') {
      setRelationshipTypes(Codes.getRelationshipTypes() || []);
    }
  }, []);

  // Computed: connection arrays
  const peopleConnections = useMemo(
    () => person?.connections?.people || [],
    [person?.connections?.people]
  );

  const vehicleConnections = useMemo(
    () => person?.connections?.vehicles || [],
    [person?.connections?.vehicles]
  );

  const businessConnections = useMemo(
    () => person?.connections?.businesses || [],
    [person?.connections?.businesses]
  );

  const clientConnections = useMemo(
    () => person?.connections?.clients || [],
    [person?.connections?.clients]
  );

  // Add connection - callback to parent
  const handleAddConnection = useCallback(
    (connectionType: string) => {
      if (!isEditMode) {
        App.showPopupNotification('Edit mode required.', 'warning');
        return;
      }
      onAddConnection?.(connectionType);
    },
    [isEditMode, onAddConnection]
  );

  // View item - callback to parent
  const handleViewItem = useCallback(
    (connectionType: string, item: any) => {
      onViewConnection?.(connectionType, item);
    },
    [onViewConnection]
  );

  // Remove connection - open confirmation
  const handleRemoveConnection = useCallback(
    (connectionType: string, item: any) => {
      if (!isEditMode) {
        App.showPopupNotification('Edit mode required.', 'warning');
        return;
      }

      let name = '';
      switch (connectionType) {
        case 'person':
          name = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'this person';
          break;
        case 'vehicle':
          name = item.plateNumber || 'this vehicle';
          break;
        case 'business':
          name = item.businessName || 'this business';
          break;
        case 'client':
          name = item.name || 'this client';
          break;
      }

      setDeleteConfirm({
        open: true,
        type: connectionType,
        item: item,
        name: name,
      });
    },
    [isEditMode]
  );

  // Confirm delete
  const confirmDelete = useCallback(() => {
    const { type, item } = deleteConfirm;
    if (!item) {
      setDeleteConfirm({ open: false, type: '', item: null, name: '' });
      return;
    }

    // Remove from connections arrays (mutating the source arrays like the Vue version)
    let connectionArray: any[] | null = null;
    let connectionNewArray: any[] | null = null;

    switch (type) {
      case 'person':
        connectionArray = person.connections?.people;
        connectionNewArray = person.connectionsNew?.people;
        break;
      case 'vehicle':
        connectionArray = person.connections?.vehicles;
        connectionNewArray = person.connectionsNew?.vehicles;
        break;
      case 'business':
        connectionArray = person.connections?.businesses;
        connectionNewArray = person.connectionsNew?.businesses;
        break;
      case 'client':
        connectionArray = person.connections?.clients;
        connectionNewArray = person.connectionsNew?.clients;
        break;
    }

    removeFromArrayById(connectionArray, item.id);
    removeFromArrayById(connectionNewArray, item.id);

    onConnectionRemoved?.({ type, id: item.id });
    setDeleteConfirm({ open: false, type: '', item: null, name: '' });
  }, [deleteConfirm, person, onConnectionRemoved]);

  // Cancel delete
  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, type: '', item: null, name: '' });
  }, []);

  return (
    <div className={`npsd-person-connections ${theme}`}>
      {/* Person Connections (with relationship) */}
      <NPSDConnectionPanel
        connections={peopleConnections}
        connectionType="person"
        title="Person Connections"
        isEditMode={isEditMode}
        showRelationship={true}
        showRoles={true}
        relationshipTypes={relationshipTypes}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Vehicle Connections */}
      <NPSDConnectionPanel
        connections={vehicleConnections}
        connectionType="vehicle"
        title="Vehicle Connections"
        isEditMode={isEditMode}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Business Connections */}
      <NPSDConnectionPanel
        connections={businessConnections}
        connectionType="business"
        title="Business Connections"
        isEditMode={isEditMode}
        showRoles={true}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Client Connections */}
      <NPSDConnectionPanel
        connections={clientConnections}
        connectionType="client"
        title="Client Connections"
        isEditMode={isEditMode}
        showRoles={true}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirm.open}
        title={`Remove ${deleteConfirm.name}?`}
        confirmText="Remove"
        cancelText="Cancel"
        autoFocus="cancel"
        scrollLock={true}
        theme={theme}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}

(window as any).NPSDPersonConnectionsReact = NPSDPersonConnections;
